import TableCommonHook from '@op/components/TableCommonHook';
import React, { useEffect, useMemo, useState } from 'react';
import { Segment, Table } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import BubbleCommon from '@op/components/BubbleCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import { PageInfoType } from '@op/types/common';
import useIntls from '@opm/hooks/useIntls';
import { SortBy } from 'tea-component/lib/table/addons';
import MemeInfoCell from './components/MemeInfoCell';
import MemeMetricsCell from './components/MemeMetricsCell';
import MemeTrendCell from './components/MemeTrendCell';
import MemeAICell from './components/MemeAICell';
import {
  VIEWS_HEADER_TIP,
  LIKES_HEADER_TIP,
  getSortOptions,
} from './constants';
import { DefaultImg } from '@op/constants/defaultImg';
import MemeTagsCell from '../components/MemeTagsCell';
import './index.scss';

const { injectable } = Table.addons;

export interface IMemesTableProps {
  sortBy?: SortBy<string>;
  setSortBy: (value: SortBy<string>) => void;
  keyword?: string;
  setKeyword: (value: string) => void;
  download: () => void;
  data: any[];
  total: number;
  loading: boolean;
  pageInfo: PageInfoType;
  changePageInfo: (value: PageInfoType) => void;
  filter: any;
  tabKey: 'views' | 'likes';
  setTabKey: (value: 'views' | 'likes') => void;
  onRowClick?: (record: any) => void;
  onTitleClick?: (record: any) => void;
  selectedMemeId?: string;
}

/**
 * Memes 表格组件
 * 支持按 Views 和 Likes 切换统计维度
 */
export default function MemesTable({
  sortBy,
  setSortBy,
  keyword = '',
  setKeyword,
  download,
  data,
  total,
  loading,
  pageInfo,
  changePageInfo,
  filter,
  tabKey,
  setTabKey,
  onRowClick,
  onTitleClick,
  selectedMemeId,
}: IMemesTableProps) {
  const { intl, isEn } = useIntls();

  // 宽屏检测：>=1900px 时拆分数值列和趋势列
  const [isWideScreen, setIsWideScreen] = useState(
    () => window.innerWidth >= 1900,
  );
  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth >= 1900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 默认排序
  const defaultSortBy: SortBy<string> = {
    order: 'desc',
    by: tabKey === 'views' ? 'views' : 'likes',
  };
  const currentSortBy = sortBy || defaultSortBy;

  // 当前使用的 header tip
  const currentHeaderTip =
    tabKey === 'views' ? VIEWS_HEADER_TIP : LIKES_HEADER_TIP;

  // 排序选项配置
  const sortOptions = useMemo(() => getSortOptions(tabKey), [tabKey]);

  // 表格列配置
  const columns = useMemo(() => {
    const list: any[] = [
      {
        key: 'meme_info',
        header: (
          <div className='dbom-memes-table-header'>
            {intl('Memes')}
            <span className='dbom-memes-table-subtitle'>
              ({intl('Click the row or the meme title to view detail')})
            </span>
          </div>
        ),
        width: 400,
        fixed: 'left',
        render: (record: any) => (
          <MemeInfoCell
            record={record}
            isEn={isEn}
            onTitleClick={onTitleClick}
          />
        ),
      },
      {
        key: 'type_element',
        header: intl('Type & Element'),
        width: 250,
        render: (record: any) => <MemeTagsCell record={record} isEn={isEn} />,
      },
      {
        key: tabKey === 'views' ? 'views' : 'likes',
        header: (
          <BubbleCommon
            placement="top"
            showIcon={false}
            tipsMap={currentHeaderTip}
          >
            <span className="dbom-meme-header-with-tip">
              {tabKey === 'views'
                ? intl('Top 500 New Video Views')
                : intl('Top 500 New Video Likes')}
            </span>
          </BubbleCommon>
        ),
        width: isWideScreen ? 230 : 300,
        align: 'right',
        render: (record: any) => (
          <MemeMetricsCell
            record={record}
            tabKey={tabKey}
            filter={filter}
            intl={intl}
            hideChart={isWideScreen}
          />
        ),
      },
    ];

    // 宽屏时追加独立趋势列
    if (isWideScreen) {
      list.push({
        key: 'trend',
        header:
          tabKey === 'views'
            ? intl('Trends in Views')
            : intl('Trends in Likes'),
        width: 200,
        render: (record: any) => (
          <MemeTrendCell record={record} tabKey={tabKey} intl={intl} />
        ),
      });
    }

    list.push({
      key: 'ai_interpretation',
      header: (
        <div className="dbom-meme-ai-header">
          <img src={DefaultImg.AI} className="dbom-meme-ai-icon" />
          <span className="dbom-meme-ai-header-text">
            {intl('AI Meme Interpretation')}
          </span>
        </div>
      ),
      width: 370,
      render: (record: any) => <MemeAICell record={record} isEn={isEn} />,
    });

    return list;
  }, [
    intl,
    isEn,
    tabKey,
    filter,
    currentHeaderTip,
    onTitleClick,
    isWideScreen,
  ]);

  // Segment 选项
  const segmentOptions = [
    { value: 'views', text: intl('Calculated by Views') },
    { value: 'likes', text: intl('Calculated by Likes') },
  ];

  // 排序列
  const sortColumns =
    tabKey === 'views' ? ['views', 'first_seen'] : ['likes', 'first_seen'];

  return (
    <CardCommon className="dbom-memes-table-card">
      <FilterBoxCommon
        left={[
          {
            key: 'segment',
            defaultValue: tabKey,
            element: (
              <Segment
                value={tabKey}
                onChange={(value) => setTabKey(value as 'views' | 'likes')}
                options={segmentOptions}
              />
            ),
          },
          {
            key: 'sortBy',
            defaultValue: currentSortBy.by,
            defaultParams: {
              options: sortOptions,
              onChange: (value: string) => {
                const option = sortOptions.find((o) => o.value === value);
                setSortBy({ by: value, order: (option?.order || 'desc') as 'asc' | 'desc' });
              },
            },
          },
        ]}
        right={[
          {
            key: 'search',
            defaultValue: keyword,
            defaultParams: {
              placeholder: intl('Search Memes'),
              onSearch: (value: string) => {
                setKeyword(value);
              },
              onClear: () => {
                setKeyword('');
              },
            },
          },
          {
            key: 'download',
            style: { marginLeft: 10 },
            event: {
              onClick: () => download(),
            },
          },
        ]}
        onChange={() => { }}
      />
      <TableCommonHook
        className="dbom-memes-table"
        sortInfo={[currentSortBy, setSortBy]}
        records={data}
        total={total}
        loading={loading}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        sortColumns={sortColumns}
        recordKey={(item) => item.meme_id || item.title + item.hot_time}
        columns={columns}
        minWidth={1200}
        tableSticky
        rowClassName={(record: any) =>
          record?.meme_id && record.meme_id === selectedMemeId
            ? 'dbom-selected-row'
            : ''
        }
        addons={[
          injectable({
            row: (_props: any, context: any) => ({
              onClick: () => onRowClick?.(context.record),
            }),
          }),
        ]}
      />
    </CardCommon>
  );
}
